import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Navbar.css";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);

  const toggleMenu = () => {
    setOpen((prev) => !prev);
    document.body.classList.toggle("nav-mobile-open", !open);
  };

  const closeMenu = () => {
    setOpen(false);
    setServicesDropdownOpen(false);
    document.body.classList.remove("nav-mobile-open");
  };

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // initialize on mount
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // keep body padding in sync with navbar height
  useEffect(() => {
    document.body.classList.toggle("nav-scrolled", scrolled);
    return () => document.body.classList.remove("nav-scrolled");
  }, [scrolled]);

  const toggleServicesDropdown = () =>
    setServicesDropdownOpen((prev) => !prev);
  const closeServicesDropdown = () => setServicesDropdownOpen(false);

  return (
    <header className={`nav ${scrolled ? "nav--scrolled" : ""}`}>
      <div className="nav__container">
        {/* Logo */}
        <Link to="/" className="nav__logo" onClick={closeMenu}>
          <img src="/logoo.png" alt="EcoClean Logo" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="nav__desktop" aria-label="Main">
          <Link to="/" className="nav__link" onClick={closeMenu}>
            Home
          </Link>
          <Link to="/about" className="nav__link" onClick={closeMenu}>
            About
          </Link>

          <div
            className="nav__link-container"
            onMouseEnter={() => setServicesDropdownOpen(true)}
            onMouseLeave={closeServicesDropdown}
          >
            <button
              className="nav__link nav__link--dropdown"
              onClick={toggleServicesDropdown}
              aria-expanded={servicesDropdownOpen}
              aria-haspopup="true"
              type="button"
            >
              Services
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`nav__dropdown-icon ${
                  servicesDropdownOpen ? "nav__dropdown-icon--open" : ""
                }`}
                aria-hidden="true"
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {servicesDropdownOpen && (
              <div className="nav__dropdown" role="menu">
                <Link
                  to="/services/household-junk"
                  className="nav__dropdown-link"
                  onClick={closeMenu}
                  role="menuitem"
                >
                  Household Junk
                </Link>
                <Link
                  to="/services/YardDebrisRemoval"
                  className="nav__dropdown-link"
                  onClick={closeMenu}
                  role="menuitem"
                >
                  Yard Debris Removal
                </Link>
                <Link
                  to="/services/garage-cleanouts"
                  className="nav__dropdown-link"
                  onClick={closeMenu}
                  role="menuitem"
                >
                  Garage Cleanouts
                </Link>
                <Link
                  to="/services/construction-debris"
                  className="nav__dropdown-link"
                  onClick={closeMenu}
                  role="menuitem"
                >
                  Construction Debris
                </Link>
                <Link
                  to="/services/Foreclosures&Evictions"
                  className="nav__dropdown-link"
                  onClick={closeMenu}
                  role="menuitem"
                >
                  Foreclosures & Evictions
                </Link>
                <Link
                  to="/services/Donationpickups"
                  className="nav__dropdown-link"
                  onClick={closeMenu}
                  role="menuitem"
                >
                  Donation Pickups
                </Link>
                <Link
                  to="/services/Junkremoval"
                  className="nav__dropdown-link"
                  onClick={closeMenu}
                  role="menuitem"
                >
                  Junk Removal
                </Link>
              </div>
            )}
          </div>

          <Link to="/contact" className="nav__link" onClick={closeMenu}>
            Contact
          </Link>
        </nav>

        {/* CTA Button (Desktop) */}
        <a
          href="https://app.squareup.com/appointments/book/41vplmwf0luh01/LTHMTFD90TMRT/start"
          className="nav__cta"
          target="_blank"
          rel="noopener noreferrer"
        >
          Schedule Pickup
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z"
              fill="currentColor"
            />
          </svg>
        </a>

        {/* Mobile Menu Button */}
        <button
          className={`nav__hamburger ${open ? "nav__hamburger--open" : ""}`}
          onClick={toggleMenu}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          type="button"
        >
          <span className="nav__hamburger-box">
            <span className="nav__hamburger-inner"></span>
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`nav__mobile ${open ? "nav__mobile--open" : ""}`}>
        <nav className="nav__mobile-links" aria-label="Mobile">
          <Link to="/" className="nav__mobile-link" onClick={closeMenu}>
            <span>Home</span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M1 6H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M6 1L11 6L6 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Link>

          <Link to="/about" className="nav__mobile-link" onClick={closeMenu}>
            <span>About</span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M1 6H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M6 1L11 6L6 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Link>

          <div className="nav__mobile-dropdown">
            <button
              className="nav__mobile-link nav__mobile-link--dropdown"
              onClick={() => setServicesDropdownOpen((prev) => !prev)}
              aria-expanded={servicesDropdownOpen}
              type="button"
            >
              <span>Services</span>
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                className={`nav__mobile-dropdown-icon ${
                  servicesDropdownOpen ? "nav__mobile-dropdown-icon--open" : ""
                }`}
                aria-hidden="true"
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <div
              className={`nav__mobile-dropdown-content ${
                servicesDropdownOpen ? "nav__mobile-dropdown-content--open" : ""
              }`}
            >
              <Link
                to="/services/household-junk"
                className="nav__mobile-dropdown-link"
                onClick={closeMenu}
              >
                Household Junk
              </Link>
              <Link
                to="/services/YardDebrisRemoval"
                className="nav__mobile-dropdown-link"
                onClick={closeMenu}
              >
                Yard Debris Removal
              </Link>
              <Link
                to="/services/garage-cleanouts"
                className="nav__mobile-dropdown-link"
                onClick={closeMenu}
              >
                Garage Cleanouts
              </Link>
              <Link
                to="/services/construction-debris"
                className="nav__mobile-dropdown-link"
                onClick={closeMenu}
              >
                Construction Debris
              </Link>
              <Link
                to="/services/Foreclosures&Evictions"
                className="nav__mobile-dropdown-link"
                onClick={closeMenu}
              >
                Foreclosures & Evictions
              </Link>
              <Link
                to="/services/Donationpickups"
                className="nav__mobile-dropdown-link"
                onClick={closeMenu}
              >
                Donation Pickups
              </Link>
              <Link
                to="/services/Junkremoval"
                className="nav__mobile-dropdown-link"
                onClick={closeMenu}
              >
                Junk Removal
              </Link>
            </div>
          </div>

          <Link to="/contact" className="nav__mobile-link" onClick={closeMenu}>
            <span>Contact</span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M1 6H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M6 1L11 6L6 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Link>
        </nav>

        {/* Mobile CTA */}
        <div className="nav__mobile-cta">
          <a
            href="https://app.squareup.com/appointments/book/41vplmwf0luh01/LTHMTFD90TMRT/start"
            className="nav__mobile-cta-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            Schedule Pickup
          </a>
        </div>
      </div>
    </header>
  );
}
